<template>
  <div class="variable-input-wrapper" :class="{ 'is-focus': isFocus }">
    <div
      ref="editor"
      class="variable-editor"
      contenteditable="true"
      @input="handleInput"
      @focus="handleFocus"
      @blur="handleBlur"
      spellcheck="false"
    ></div>
    <div class="placeholder" v-if="showPlaceholder">{{ placeholder }}</div>
  </div>
</template>

<script>
export default {
  name: 'VariableInput',
  props: {
    value: {
      type: String,
      default: ''
    },
    variables: {
      type: Array,
      default: () => []
    },
    placeholder: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      isFocus: false,
      isLocked: false,
      lastHtml: ''
    }
  },
  computed: {
    showPlaceholder () {
      return !this.value && !this.isFocus
    },
    variableMap () {
      const map = {}
      if (this.variables) {
        this.variables.forEach(v => {
          map[v.name] = v
        })
      }
      return map
    }
  },
  watch: {
    value (val) {
      if (this.isLocked) return
      // Check if semantic content changed (to avoid cursor jumping on identical re-render)
      // For simplicity, we just render if we are not locked (user typing locks it temporary, but here we rely on isLocked flag which we don't set during 'input' for external updates)
      // Actually, if parent updates, we should re-render.
      // But we need to compare parsed value to avoid loop?
      // Simple approach: Only re-render if serializing current DOM doesn't match new value.
      const currentVal = this.serializeContent()
      if (val !== currentVal) {
        this.renderContent(val)
      }
    },
    variables: {
      deep: true,
      handler () {
        // Refresh tags labels if variables def changes
        this.renderContent(this.value)
      }
    }
  },
  mounted () {
    this.renderContent(this.value)
  },
  methods: {
    handleFocus () {
      this.isFocus = true
      this.isLocked = true
    },
    handleBlur () {
      this.isFocus = false
      this.isLocked = false
      // Force sync final content
      this.$emit('input', this.serializeContent())
    },
    handleInput () {
      const val = this.serializeContent()
      this.$emit('input', val)
    },
    insertVariable (variable) {
      if (!variable || !variable.name) return

      // Create tag element
      // Create tag element
      const span = document.createElement('span')
      span.className = 'var-tag'
      span.contentEditable = false
      span.dataset.key = variable.name

      const labelSpan = document.createElement('span')
      labelSpan.className = 'var-tag-label'
      labelSpan.innerText = variable.label + ': '

      const valueSpan = document.createElement('span')
      valueSpan.className = 'var-tag-value'
      valueSpan.innerText = variable.defaultValue || variable.name

      span.appendChild(labelSpan)
      span.appendChild(valueSpan)

      this.$refs.editor.focus()

      const sel = window.getSelection()
      if (sel.rangeCount > 0) {
        const range = sel.getRangeAt(0)
        // Check if range is inside our editor
        if (this.$refs.editor.contains(range.commonAncestorContainer)) {
          range.deleteContents()
          range.insertNode(span)

          // Move cursor after span
          range.setStartAfter(span)
          range.setEndAfter(span)
          sel.removeAllRanges()
          sel.addRange(range)
        } else {
          // Append to end if lost focus/selection
          this.$refs.editor.appendChild(span)
          // Move cursor to end
          const newRange = document.createRange()
          newRange.selectNodeContents(this.$refs.editor)
          newRange.collapse(false)
          sel.removeAllRanges()
          sel.addRange(newRange)
        }
      } else {
        this.$refs.editor.appendChild(span)
      }

      // Add a zero-width space or space after tag to allow typing easily?
      // Browsers often have trouble typing after contentEditable=false element.
      // Inserting a space usually helps.
      const space = document.createTextNode('\u00A0') // non-breaking space
      if (span.nextSibling) {
        span.parentNode.insertBefore(space, span.nextSibling)
      } else {
        this.$refs.editor.appendChild(space)
      }

      // Move cursor after space
      const sel2 = window.getSelection()
      const range2 = document.createRange()
      range2.setStartAfter(space)
      range2.setEndAfter(space)
      sel2.removeAllRanges()
      sel2.addRange(range2)

      this.handleInput()
    },
    serializeContent () {
      if (!this.$refs.editor) return ''
      let text = ''
      const nodes = this.$refs.editor.childNodes
      nodes.forEach(node => {
        if (node.nodeType === Node.TEXT_NODE) {
          text += node.textContent
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          if (node.classList.contains('var-tag')) {
            text += `{{${node.dataset.key}}}`
          } else {
            // Handle div/br for line breaks if generic contenteditable behavior adds them
            if (node.tagName === 'DIV') {
              // DIV usually implies new line
              // Recursive? Or just simple text extraction?
              // For simplicity, treat as newline + content
              text += '\n' + node.textContent // simplified
            } else if (node.tagName === 'BR') {
              text += '\n'
            } else {
              text += node.textContent
            }
          }
        }
      })
      // Clean up common contenteditable artifacts like non-breaking spaces if needed
      // text = text.replace(/\u00A0/g, ' ')
      return text
    },
    renderContent (val) {
      if (!this.$refs.editor) return
      if (!val) {
        this.$refs.editor.innerHTML = ''
        return
      }

      const parts = val.split(/(\{\{[^}]+\}\})/g)
      const html = parts.map(part => {
        const match = part.match(/^\{\{([^}]+)\}\}$/)
        if (match) {
          const key = match[1]
          const variable = this.variableMap[key]
          const label = variable ? variable.label : key
          const value = variable && variable.defaultValue ? variable.defaultValue : key

          return `<span class="var-tag" contenteditable="false" data-key="${key}">` +
                 `<span class="var-tag-label">${label}: </span>` +
                 `<span class="var-tag-value">${value}</span>` +
                 `</span>`
        }
        return part.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br>')
      }).join('')

      this.$refs.editor.innerHTML = html
    }
  }
}
</script>

<style scoped>
.variable-input-wrapper {
  position: relative;
  width: 100%;
  border: 1px solid #DCDFE6;
  border-radius: 4px;
  background-color: #FFF;
  transition: border-color .2s cubic-bezier(.645,.045,.355,1);
  min-height: 100px; /* autosize min-height */
  cursor: text;
}
.variable-input-wrapper.is-focus {
  border-color: #409EFF;
}
.variable-editor {
  padding: 5px 15px;
  min-height: 100px;
  outline: none;
  font-size: inherit;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-all;
}
.placeholder {
  position: absolute;
  top: 5px;
  left: 15px;
  color: #C0C4CC;
  pointer-events: none;
}
/* Deep selector for dynamically generated tags if scoped */
.variable-editor >>> .var-tag {
  display: inline-block;
  padding: 0 6px;
  margin: 0 2px;
  background-color: #ecf5ff;
  border: 1px solid #d9ecff;
  border-radius: 4px;
  color: #409EFF;
  font-size: 12px;
  line-height: 20px;
  height: 22px;
  white-space: nowrap;
  vertical-align: middle;
}
.variable-editor >>> .var-tag-label {
  font-size: 0.85em;
  color: #909399;
  margin-right: 2px;
}
.variable-editor >>> .var-tag-value {
  font-weight: 500;
}
</style>
